/**
 * WhatsAppButton — Floating fixed button (bottom-right) that opens a WhatsApp
 * chat to the restaurant phone number using the official `wa.me` link.
 *
 * Behaviour:
 *  - Reads the WhatsApp number from the global Contenido context (config.whatsapp).
 *  - Falls back to the public restaurant phone (+34 925 48 46 33) if not set.
 *  - Sanitises the number (digits only, as required by wa.me).
 *  - Renders the official WhatsApp glyph as inline SVG (lucide-react does not
 *    ship brand icons, so we keep the asset local for zero extra deps).
 *  - Soft pulsing halo + hover scale for affordance.
 *
 * Sizing and placement (mobile-first):
 *  - 52×52 px on small phones (<sm). Larger button (64×64) on sm+.
 *  - Anchored at `bottom-5 right-5` on mobile, `bottom-8 right-8` on sm+ to
 *    keep clear of native iOS / Android system bars.
 *  - z-index 40 (above content, below the navbar overlay z-50).
 *
 * Accessibility:
 *  - `aria-label` describes the action ("Chatear por WhatsApp…").
 *  - Color #25D366 is the brand color (>=AA contrast with white glyph).
 *
 * Visible on every public page (mounted in PublicLayout).
 */
import { useContenido } from "@/context/ContenidoContext";
import { motion } from "framer-motion";

/**
 * Official WhatsApp glyph — single-color, verified path centred inside a
 * 24×24 viewBox so it scales cleanly to any pixel size.
 */
function WhatsAppGlyph({ size = 26, color = "#FFFFFF" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={color}
            aria-hidden="true"
        >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
        </svg>
    );
}

export default function WhatsAppButton() {
    const { config } = useContenido();
    // WhatsApp `wa.me` deeplinks require digit-only international format.
    // Default keeps the verified booking number used in production.
    const number = (config?.whatsapp || "+34661428062").replace(/[^0-9]/g, "");

    return (
        <motion.a
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.5, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            href={`https://wa.me/${number}`}
            target="_blank"
            rel="noreferrer noopener"
            aria-label="Chatear por WhatsApp con Restaurante Museo Tenería"
            data-testid="whatsapp-floating-button"
            data-cursor="hover"
            className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-40 group"
        >
            {/* Pulsing halo — purely decorative */}
            <span
                aria-hidden
                className="absolute inset-0 rounded-full animate-ping"
                style={{ background: "#25D366", opacity: 0.22 }}
            />
            {/* Solid pill */}
            <span
                className="relative w-[52px] h-[52px] sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                style={{
                    background: "#25D366",
                    boxShadow: "0 10px 30px rgba(37,211,102,0.35)",
                }}
            >
                <WhatsAppGlyph size={26} />
            </span>
        </motion.a>
    );
}
